import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import {
    SpreadsheetGrid,
    SpreadsheetGridHandle,
} from "@components/spreadsheet/SpreadsheetGrid";
import { SpreadsheetData } from "@components/spreadsheet/types";
import { FinancialDocumentUploadDropzone } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadDropzone";
import { TriangleIcon, UploadIcon } from "@icons";
import {
    MIGRATION_GRID_TEMPLATE_COLUMNS,
    MIGRATION_UNVERIFIED_COLUMNS,
    MIGRATION_UPLOAD_COLUMNS,
} from "./migrationFileUploadColumns";
import styles from "./MigrationFileUploadTaskBody.module.scss";

interface MigrationGridsProps {
    mainData: SpreadsheetData;
    unverifiedData: SpreadsheetData;
}

export const MigrationGrids: React.FC<MigrationGridsProps> = ({
    mainData,
    unverifiedData,
}) => {
    const [isUnverifiedOpen, setIsUnverifiedOpen] = useState(true);
    const mainGridRef = useRef<SpreadsheetGridHandle>(null);
    const unverifiedGridRef = useRef<SpreadsheetGridHandle>(null);
    const [pendingFocusCol, setPendingFocusCol] = useState<number | null>(null);

    const handleMainGridNavigatePastEnd = useCallback((col: number) => {
        setIsUnverifiedOpen(true);
        setPendingFocusCol(col);
    }, []);

    useEffect(() => {
        if (pendingFocusCol === null) {
            return;
        }
        const col = pendingFocusCol;
        setPendingFocusCol(null);
        unverifiedGridRef.current?.focus({ row: 0, col });
    }, [pendingFocusCol]);

    const handleUnverifiedGridNavigatePastStart = useCallback(
        (col: number) => {
            if (mainData.length > 0) {
                mainGridRef.current?.focus({ row: mainData.length - 1, col });
            }
        },
        [mainData.length],
    );

    return (
        <FinancialDocumentUploadDropzone
            dropzoneClassName={styles.tableSection}
            cover={
                <div className={styles.dropCover}>
                    <UploadIcon />
                    <span>Drop files to upload</span>
                </div>
            }
        >
            <SpreadsheetGrid
                data={mainData}
                columns={MIGRATION_UPLOAD_COLUMNS}
                gridTemplateColumns={MIGRATION_GRID_TEMPLATE_COLUMNS}
                appearance="list"
                readonly
                noOuterBorder
                disableDragFill
                rowHoverHighlight
                onNavigatePastEnd={
                    unverifiedData.length > 0
                        ? handleMainGridNavigatePastEnd
                        : undefined
                }
                gridHandle={mainGridRef}
            />

            <div
                className={styles.unverifiedHeader}
                onClick={() => setIsUnverifiedOpen((open) => !open)}
            >
                <TriangleIcon
                    className={classNames(
                        styles.unverifiedTriggerIcon,
                        "icon-color-gray-500",
                        { [styles.expanded]: isUnverifiedOpen },
                    )}
                    width={12}
                    height={12}
                />
                <span>Unverified ({unverifiedData.length})</span>
            </div>

            {isUnverifiedOpen && unverifiedData.length > 0 && (
                <SpreadsheetGrid
                    className={styles.unverifiedGrid}
                    data={unverifiedData}
                    columns={MIGRATION_UNVERIFIED_COLUMNS}
                    gridTemplateColumns={MIGRATION_GRID_TEMPLATE_COLUMNS}
                    appearance="list"
                    hideHeaderRow
                    readonly
                    noOuterBorder
                    disableDragFill
                    rowHoverHighlight
                    onNavigatePastStart={handleUnverifiedGridNavigatePastStart}
                    gridHandle={unverifiedGridRef}
                />
            )}
        </FinancialDocumentUploadDropzone>
    );
};
